import type { LetterheadLayout } from '../../../types/facility.type'
import parse from 'html-react-parser'

type ReportLetterheadProps = {
  letterheadHtml?: string | null
  logoUrl?: string
  logoAlt?: string
  layout: LetterheadLayout
  className?: string
}

const logoImgClass = 'max-h-20 w-auto max-w-[16rem] object-contain print:max-h-14'
const letterheadProseClass = 'prose prose-sm max-w-none text-[#333] [&_p]:my-1 [&_a]:text-[#008A00]'

function Logo({ src, alt }: { src: string; alt: string }) {
  return <img alt={alt} className={logoImgClass} src={src} />
}

function LetterheadHtml({ html, align }: { html: string; align: 'left' | 'right' | 'center' }) {
  const alignClass = align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left'
  return <div className={`${letterheadProseClass} ${alignClass}`}>{parse(html)}</div>
}

const ReportLetterhead = ({
  letterheadHtml,
  logoUrl,
  logoAlt = 'Facility logo',
  layout,
  className = ''
}: ReportLetterheadProps) => {
  const hasLogo = Boolean(logoUrl)
  const hasLetterhead = Boolean(letterheadHtml?.trim())

  if (!hasLogo && !hasLetterhead) return null

  if (layout === 'stacked') {
    return (
      <header className={`mb-6 flex flex-col items-center gap-3 print:mb-2 ${className}`}>
        {hasLogo && logoUrl ? <Logo alt={logoAlt} src={logoUrl} /> : null}
        {hasLetterhead && letterheadHtml ? <LetterheadHtml align="center" html={letterheadHtml} /> : null}
      </header>
    )
  }

  if (layout === 'logo_right') {
    return (
      <header
        className={`mb-6 flex flex-col items-stretch gap-4 sm:flex-row sm:items-start sm:justify-between print:mb-2 ${className}`}
      >
        {hasLetterhead && letterheadHtml ? (
          <div className="min-w-0 flex-1">
            <LetterheadHtml align="left" html={letterheadHtml} />
          </div>
        ) : (
          <div className="flex-1" />
        )}
        {hasLogo && logoUrl ? (
          <div className="shrink-0 self-center sm:self-start">
            <Logo alt={logoAlt} src={logoUrl} />
          </div>
        ) : null}
      </header>
    )
  }

  // logo_left (default)
  return (
    <header
      className={`mb-6 flex flex-col items-stretch gap-4 sm:flex-row sm:items-start sm:justify-between print:mb-2 ${className}`}
    >
      {hasLogo && logoUrl ? (
        <div className="shrink-0 self-center sm:self-start">
          <Logo alt={logoAlt} src={logoUrl} />
        </div>
      ) : null}
      {hasLetterhead && letterheadHtml ? (
        <div className="min-w-0 flex-1">
          <LetterheadHtml align="right" html={letterheadHtml} />
        </div>
      ) : null}
    </header>
  )
}

export default ReportLetterhead
